import React, { useState } from 'react';
import { KModal } from '@/ui-library/components/molecules/KModal/KModal';
import { Requisition } from '@/components/shared/requisitions/associate-req/associateReq.interface';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import { AssociateReqContainer } from '@/components/shared/requisitions/associate-req/components/AssociateReqContainer';

interface AssociateReqModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssociate?: (reqs: Requisition[]) => void;
  multiSelect?: boolean;
  selectedList?: Requisition[];
  offerId?: string;
}

export const AssociateReqModal: React.FC<AssociateReqModalProps> = ({
  isOpen,
  onClose,
  onAssociate,
  multiSelect = false,
  selectedList = [],
  offerId,
}) => {
  const [selectedReqs, setSelectedReqs] = useState<Requisition[]>(selectedList);
  const [reqListState, setReqListState] = useState<Requisition[]>([]);
  const [showGroupDialog, setShowGroupDialog] = useState<number | null>(null);
  const users = useSelector(selectUsers);

  const handleSelectReq = (req: Requisition) => {
    // If dialog is already showing for this req, close it and remove from selection
    if (showGroupDialog === req.id) {
      setShowGroupDialog(null);
      // Remove this requisition from selection when closing dialog without choosing
      setSelectedReqs((prev) => prev?.filter((item) => item.id !== req.id));
      return;
    }

    // Check if this requisition is already selected
    const isAlreadySelected = selectedReqs?.some((item) => item.id === req.id);

    // Check if there's an active dialog for the same group
    const dialogReq = reqListState?.find((r) => r.id === showGroupDialog);
    const isDialogForSameGroup =
      dialogReq &&
      req.requisition_group_id &&
      dialogReq.requisition_group_id === req.requisition_group_id;

    // If multiSelect and requisition is part of a group AND not already selected
    // AND there's no dialog already open for this group, show confirmation dialog
    if (
      multiSelect &&
      req.requisition_group_id &&
      !isAlreadySelected &&
      !isDialogForSameGroup
    ) {
      // Add the requisition to selection immediately when showing dialog
      setSelectedReqs((prev) => [...prev, req]);
      setShowGroupDialog(req.id);
      return;
    }

    // Otherwise, handle selection (toggle)
    setSelectedReqs((prev) => {
      if (!multiSelect) {
        // Single selection mode - replace current selection
        const isSelected = prev?.some((item) => item.id === req.id);
        return isSelected ? [] : [req];
      } else {
        // Multi selection mode - toggle selection
        const isSelected = prev?.some((item) => item.id === req.id);
        return isSelected
          ? prev?.filter((item) => item.id !== req.id)
          : [...prev, req];
      }
    });
  };

  // Handle selecting all group requisitions
  const handleSelectAllGroup = (req: Requisition) => {
    const groupRequisitions = reqListState?.filter(
      (r) =>
        r.requisition_group_id === req.requisition_group_id &&
        r.requisition_group_id != null // eslint-disable-line eqeqeq -- nullish (null|undefined) group-id check
    );

    // Select all group requisitions
    setSelectedReqs((prev) => {
      const newSelections = [...prev];
      groupRequisitions?.forEach((groupReq) => {
        const isSelected = newSelections.some(
          (item) => item.id === groupReq.id
        );
        if (!isSelected) {
          newSelections.push(groupReq);
        }
      });
      return newSelections;
    });
  };

  // Handle selecting single requisition
  const handleSelectSingle = (req: Requisition) => {
    const groupRequisitions = reqListState?.filter(
      (r) =>
        r.requisition_group_id === req.requisition_group_id &&
        r.requisition_group_id != null // eslint-disable-line eqeqeq -- nullish (null|undefined) group-id check
    );

    setSelectedReqs((prev) => {
      // Remove all group requisitions from selection
      let newSelections = prev?.filter(
        (item) =>
          !groupRequisitions?.some((groupReq) => groupReq.id === item.id)
      );

      // Add only the clicked requisition
      if (!newSelections.some((item) => item.id === req.id)) {
        newSelections = [...newSelections, req];
      }

      return newSelections;
    });
  };

  const handleAssociate = () => {
    if (onAssociate) {
      onAssociate(selectedReqs);
    }

    onClose();
  };

  return (
    <KModal
      isOpen={isOpen}
      onClose={onClose}
      title="Associate requisitions"
      footer={reqListState?.length > 0 ? true : false}
      ctaLabel="Associate requisitions"
      ctaAction={handleAssociate}
      ctaButtonProps={{
        isDisabled: !selectedReqs || selectedReqs?.length === 0,
      }}
      modalBodyProps={{
        px: '0',
        height: '50%',
      }}
      modalContentProps={{
        maxW: '800px',
      }}
    >
      <AssociateReqContainer
        selectedReqs={selectedReqs}
        users={users}
        onSelectReq={handleSelectReq}
        multiSelect={multiSelect}
        setReqListState={setReqListState}
        offerId={offerId}
        showGroupDialog={showGroupDialog}
        onSelectAllGroup={handleSelectAllGroup}
        onSelectSingle={handleSelectSingle}
      />
    </KModal>
  );
};
